/* @jsxImportSource react */
'use client';

import { forwardRef, useCallback, useState } from 'react';

import {
  Field,
  fieldLabel,
  type FormControlBaseProps,
  type FormControlLabelProps,
} from '../field/field';
import { Input, type TextInputProps } from '../input/input';
import {
  countTelDigits,
  E164_MAX_DIGITS,
  formatTelInput,
  restrictTelInput,
  telCaretPosition,
  unformatTel,
} from './tel-format';

/** @internal */
export const ATOMS_PHONE_INPUT_VARIANTS = {} as const;
/** @internal */
export const ATOMS_PHONE_INPUT_DEFAULT_VARIANTS = null;

export type PhoneInputProps = Omit<TextInputProps, 'type' | 'value' | 'defaultValue'> & {
  /**
   * Current value, as an unformatted number — the separators are added for
   * display. A stored E.164 value renders as a national number.
   */
  value?: string;
  /** Uncontrolled initial value, read the same way as `value`. */
  defaultValue?: string;
  /**
   * Fires on every change with the number stripped of presentation: digits,
   * keeping a leading `+`. Read this rather than `event.target.value`, which
   * carries the formatted display text.
   */
  onValueChange?: (value: string) => void;
  /**
   * Digits the field accepts. A keystroke that would exceed this is refused, so a
   * number already entered is never rewritten to make room.
   *
   * @default 15 — the E.164 maximum, so no real number is ever cut short.
   */
  maxDigits?: number;
};

/**
 * Phone field. Separators are presentation: the field shows `(408) 370-7063`
 * while `onValueChange` reports `4083707063`, so a caller stores the number it
 * collected and never has to strip punctuation back out. A national number is
 * read as US; a `+`-prefixed value keeps its dialing code and is grouped for its
 * own country.
 *
 * A character that cannot be part of a number is refused at the keystroke rather
 * than shown and left for validation to reject afterwards.
 *
 * Lives apart from `Input` so that only the phone fields pull in the phone
 * number library.
 */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>((props, ref) => {
  const { onChange, onValueChange, value, defaultValue, maxDigits, ...inputProps } = props;
  const effectiveMaxDigits = maxDigits ?? E164_MAX_DIGITS;

  // Falls back to the incoming text: an unparseable `value` (a junk prefill) is
  // shown as it arrived rather than blanked. It survives display, not editing —
  // an insertion is filtered across the whole field, so the first keystroke into
  // a prefill the formatter could not read replaces it rather than extends it.
  const asDisplay = useCallback((raw: string) => formatTelInput(raw) || raw, []);

  const [display, setDisplay] = useState(() => asDisplay(value ?? defaultValue ?? ''));

  // A `value` the caller changed underneath us (a reset, a prefill arriving late)
  // has to win; the same number echoed back from `onValueChange` must not, or it
  // would rewrite the separators the user is typing between.
  const [lastValue, setLastValue] = useState(value);
  if (value !== undefined && value !== lastValue) {
    setLastValue(value);
    if (value !== unformatTel(display)) {
      setDisplay(asDisplay(value));
    }
  }

  const handleChange = useCallback<NonNullable<TextInputProps['onChange']>>(
    (event) => {
      const input = event.currentTarget;
      const typed = input.value;
      const { nativeEvent } = event;
      // `inputType` distinguishes a deletion from an insertion, but only
      // InputEvent carries it — a programmatic change event has no such hint.
      const inputType =
        'inputType' in nativeEvent && typeof nativeEvent.inputType === 'string'
          ? nativeEvent.inputType
          : '';
      const deleting = inputType.startsWith('delete');

      // An insertion into a field that is already full is refused outright rather
      // than trimmed to fit. Trimming counts digits left to right across the whole
      // field, so a keystroke at the front would silently drop the digit at the
      // back — mutating a number the user already finished instead of ignoring
      // the key. A field with room left trims instead, so an over-long paste
      // lands as much as fits rather than vanishing with no sign it was read.
      if (
        !deleting &&
        countTelDigits(typed) > effectiveMaxDigits &&
        countTelDigits(display) >= effectiveMaxDigits
      ) {
        const inserted = Math.max(typed.length - display.length, 0);
        const caret = Math.min(
          Math.max((input.selectionStart ?? display.length) - inserted, 0),
          display.length,
        );
        input.value = display;
        input.setSelectionRange(caret, caret);
        return;
      }

      // Filtered before formatting, so a rejected character never reaches the
      // screen — but only on insertion. A deletion can only remove characters, so
      // it has nothing to refuse, and filtering its result would rewrite text the
      // user never touched: one backspace would re-cap a longer prefill, or strip
      // an unparseable one the field is meant to show as it arrived.
      const accepted = deleting
        ? typed
        : restrictTelInput(typed, { maxDigits: effectiveMaxDigits });
      const formatted = formatTelInput(accepted, { deleting });

      if (formatted !== typed) {
        const caret = telCaretPosition(typed, input.selectionStart ?? typed.length, formatted);
        // Write to the node before the re-render: assigning `value` drops the
        // caret at the end, so put it back where the digits say, and React finds
        // the DOM already holding what it is about to render.
        input.value = formatted;
        input.setSelectionRange(caret, caret);
      }
      setDisplay(formatted);
      setLastValue(unformatTel(formatted));

      onValueChange?.(unformatTel(formatted));
      onChange?.(event);
    },
    [onChange, onValueChange, effectiveMaxDigits, display],
  );

  return <Input ref={ref} {...inputProps} type="tel" value={display} onChange={handleChange} />;
});

PhoneInput.displayName = 'PhoneInput';

export type FormInputPhoneProps = FormControlBaseProps &
  FormControlLabelProps &
  Omit<
    PhoneInputProps,
    | 'name'
    | 'value'
    | 'onChange'
    | 'onValueChange'
    | 'onBlur'
    | 'error'
    | 'aria-label'
    | 'aria-labelledby'
  >;

/**
 * `PhoneInput` bound to a `Form.Root` field, the counterpart to `Form.Input`.
 * The field stores the unformatted number, so validation and submit see digits
 * rather than the separators on screen.
 *
 * Reachable as `Form.InputPhone`. Defined here rather than in `form.tsx` so the
 * standalone `PhoneInput` stays importable without the form layer; the reverse
 * dependency is what puts the phone number library in `@opendoor/atoms/form`.
 */
export function FormInputPhone({
  name,
  label,
  'aria-label': ariaLabel,
  description,
  required,
  id,
  ...inputProps
}: FormInputPhoneProps) {
  return (
    <Field
      name={name}
      label={fieldLabel(label, ariaLabel)}
      description={description}
      required={required}
      id={id}
    >
      {(f) => (
        <PhoneInput
          {...inputProps}
          // `f.id` matches the Field label's `htmlFor`, associating the visible
          // label with the input (and satisfying Input's accessible-name type).
          id={f.id}
          name={f.name}
          value={typeof f.value === 'string' ? f.value : ''}
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- runs inside Field's render prop where no hook can hoist it, and PhoneInput isn't memoized so the fresh identity is harmless
          onValueChange={(next: string) => f.setValue(next)}
          onBlur={f.onBlur}
          error={f.hasError}
        />
      )}
    </Field>
  );
}

FormInputPhone.displayName = 'Form.InputPhone';
